import { useLatestFn, useResizeObserver, χref } from '@studio/react-utils/hooks';
import { isNotNil } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { RefObject } from 'react';
import { useLayoutEffect, useState, useEffect } from 'react';
import type { Layout } from './types.js';
import { computeLayout } from './utils.js';
import { useArrangement, useDeviceIds, useRoom, useSelectedVideo } from '#~/room/hooks.js';
import { Arrangement } from '#~/video/index.js';
import { useDepot } from '#~/depot/hooks.js';

/** @internal */
export const useLayout = (ref: RefObject<HTMLElement | null>): T_Maybe<Layout> => {
  const { videoIds } = useDeviceIds();
  const [layout, setLayout] = useState<Layout>();
  const [selectedId] = useSelectedVideo();
  const [arrangement] = useArrangement();

  const handleLayout = useLatestFn(() => {
    const clientRect = χref(ref)?.getBoundingClientRect();

    if (!clientRect || !videoIds.length || !clientRect.width || !clientRect.height) {
      setLayout(undefined);

      return;
    }

    const nextLayout = computeLayout(
      clientRect,
      arrangement === Arrangement.Multiple ? videoIds : [selectedId].filter(isNotNil)
    );

    setLayout(nextLayout ? { ...nextLayout, arrangement } : undefined);
  });

  // Compute layout when size changes
  useResizeObserver(ref, handleLayout);

  // Compute the layout when the set of videos or arrangement changes.
  useLayoutEffect(
    () => {
      handleLayout();
    },

    // eslint-disable-next-line react-hooks-addons/no-unused-deps
    [arrangement, videoIds, selectedId]
  );

  const roomId = useRoom((µ) => µ.id);
  const canControl = useDepot((u) => u.roomRecords[roomId]?.permissions.canControl);

  useEffect(() => {
    setTimeout(() => {
      handleLayout();
    });
  }, [canControl, handleLayout]);

  return layout;
};
